import { useTheme } from "react-native-paper";
import React from "react";
import { StyleSheet, View } from "react-native";
import { AntDesign } from "@expo/vector-icons";
import { Text } from "../text/text";
import { useNavigation } from "@react-navigation/native";
import { theme, ThemeType } from "../../theme/theme";
import { Stack } from "../../components/layout/stack";
import { PaymentMethodCard } from "./payment-method-card";
import { AppMode, PaymentSourceType } from "../../enums";
import { AddPaymentMethodCard } from "../add-payment-method-card/add-payment-method-card";
import { translate } from "../../i18n";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/reducers";

export const MONEYGRAM_STATIC_INFO = "MONEYGRAM_STATIC_INFO";

interface PropertyFees extends Record<string, any> {
  // In cents
  certified_check_limit: number;
  id: string | number;
  // in cents
  non_certified_limit: number;
  // in cents
  rent_ach_minimum: number;
  // floating number as percent 2.25
  rent_cc_percent: number;
  // floating number as percent
  rent_certified_cc_percent: number;
  rent_domuso_cash_minimum: number;
}

interface PaymentMethodCardGroupProps extends Record<string, any> {
  paymentMethods: any[];
  paymentType: PaymentSourceType;
  fees: Partial<PropertyFees>;
  useOnlyActive: boolean;
  selectAPaymentMethodProps: any;
  shouldDisableCC?: boolean;
  allowCC?: boolean;
  allowACH?: boolean;
  certifiedDateRange: any;
  addMoneygramMethod?: boolean;
  selected: any;
  emitSelected: any;
}

export const PaymentMethodCardGroup = ({
  paymentMethods = [],
  paymentType,
  useOnlyActive = true,
  fees = {},
  allowedPayments,
  shouldDisableCC = false,
  allowCC = true,
  allowACH = true,
  selectAPaymentMethodProps,
  certifiedDateRange = {},
  selected,
  emitSelected,
}: PaymentMethodCardGroupProps) => {
  const navigation = useNavigation();
  const theme = useTheme() as ThemeType;
  const iconSize = theme.iconSize.large;
  const styles = getStyles();
  const propertyFees = useSelector((state: RootState) => state.reservation.propertyFees);
  const appMode = useSelector((state: RootState) => state.reservation.appMode);

  const isRecoveryPayment = appMode === AppMode.DCASH_RECOVERY;
  if (paymentType === PaymentSourceType.CREDIT_CARD && !allowCC) {
    paymentMethods = [];
  }
  if (paymentType === PaymentSourceType.BANK_ACCOUNT && !allowACH) {
    paymentMethods = [];
  }
  if (paymentType) {
    paymentMethods = paymentMethods.filter((p) => p.type === paymentType);
  }
  if (useOnlyActive) {
    paymentMethods = paymentMethods.filter((p) => p.active);
  }

  const onPressBA = () => {
    navigation.navigate("AddBankAccount", {
      ...selectAPaymentMethodProps,
      fromSelectPayment: true,
    });
  };
  const onPressCC = () => {
    navigation.navigate("AddCreditCard", {
      ...selectAPaymentMethodProps,
      fromSelectPayment: true,
    });
  };

  // TODO: money library
  const formattedFeeBA = isRecoveryPayment
    ? 0
    : Number(propertyFees?.bankAccount?.fee / 100).toFixed(2) || 0;
  // non certified
  const nonCertifiedCCFee = propertyFees?.nonCertifiedCCDebitCard?.feeValue * 100 || 0;
  const formattedFeeCC = isRecoveryPayment ? 0 : nonCertifiedCCFee;

  let ccDisabledMessage = !allowCC ? "notAllowedCC" : "disabledCC";

  const renderPaymentMethodCards = () => {
    return (
      <>
        {paymentMethods.map((props: any, index: number) => (
          <View key={index} style={{ marginBottom: theme.spacing["medium"] }}>
            <PaymentMethodCard
              key={index}
              cardholder={props.cardholder}
              paymentType={props.type}
              allowedPayments={allowedPayments}
              shouldDisableCC={shouldDisableCC}
              allowCC={allowCC}
              allowACH={allowACH}
              {...props}
              certifiedDateRange={certifiedDateRange}
              index={index}
              iconSize={iconSize}
              fees={fees}
              emitSelected={emitSelected}
              selected={selected}
            />
          </View>
        ))}
      </>
    );
  };

  const renderFeeNotification = (text: string) => {
    return (
      <View style={styles.fee}>
        <AntDesign
          accessible={true}
          accessibilityLabel={
            paymentType === PaymentSourceType.CREDIT_CARD
              ? translate("homeStack.creditCardInformation")
              : translate("homeStack.bankAccountInformation")
          }
          style={styles.feeIcon}
          name="exclamationcircleo"
          size={theme.iconSize.smaller}
        />
        <Text style={styles.feeText}>{text}</Text>
      </View>
    );
  };

  return (
    <>
      {paymentType === PaymentSourceType.CREDIT_CARD ? (
        <>
          <Text tx="homeStack.creditCard" style={styles.title} />
          {renderFeeNotification(`${formattedFeeCC}% Platform Fee`)}
          {renderPaymentMethodCards()}
          {!shouldDisableCC && allowCC ? (
            <AddPaymentMethodCard
              onPress={onPressCC}
              type={PaymentSourceType.CREDIT_CARD}
              isCertified={false}
            />
          ) : (
            <Stack space="tiny">
              <Text style={styles.disabledText} tx={`componentStatic.${ccDisabledMessage}.line1`} />
              <Text style={styles.disabledText} tx={`componentStatic.${ccDisabledMessage}.line2`} />
            </Stack>
          )}
        </>
      ) : (
        <>
          <Text tx="homeStack.bankAccount" style={styles.title} />
          {renderFeeNotification(`$${formattedFeeBA} Platform Fee`)}
          {renderPaymentMethodCards()}
          {allowACH ? (
            <AddPaymentMethodCard
              onPress={onPressBA}
              type={PaymentSourceType.BANK_ACCOUNT}
              isCertified={false}
            />
          ) : (
            <Stack space="tiny">
              <Text style={styles.disabledText} tx="componentStatic.notAllowedACH.line1" />
              <Text style={styles.disabledText} tx="componentStatic.notAllowedACH.line2" />
            </Stack>
          )}
        </>
      )}
    </>
  );
};

const getStyles = () => {
  return StyleSheet.create({
    title: {
      fontSize: 14,
      fontWeight: "bold",
    },
    fee: {
      display: "flex",
      alignItems: "center",
      flexDirection: "row",
      marginTop: 6,
      marginBottom: 8,
    },
    feeIcon: {
      marginRight: 5,
    },
    feeText: {
      fontSize: 12,
    },
    disabledText: {
      color: theme.colors.darkBg,
      fontWeight: "600",
    },
  });
};
